import { FC, useMemo } from "react";
import classNames from "classnames";
import { FormCheckbox } from "../FormFields";
import { formatName, shipmentStatusFormat } from "../../utils/format";
import { PaymentMode, DocumentStatus } from "../../interfaces";
import { StoreShipmentInterface } from "../../interfaces/Dtos";

export interface ShipmentTableCheckProps {
  fixed?: string;
  shipments: StoreShipmentInterface[];
  selectedShipmentNumbers: string[];
  setSelectedShipmentNumbers?: (ids: string[]) => void;
}

const ShipmentTableCheck: FC<ShipmentTableCheckProps> = ({
  fixed,
  shipments,
  selectedShipmentNumbers,
  setSelectedShipmentNumbers = () => {},
}) => {
  const handleSelectAll = () => {
    if (selectedShipmentNumbers.length === shipments.length) {
      setSelectedShipmentNumbers(fixed ? [fixed] : []);
    } else {
      setSelectedShipmentNumbers(shipments.map((s) => s.shipmentNumber));
    }
  };

  const shipmentFixed = useMemo(() => {
    return shipments.find((s) => s.shipmentNumber === fixed);
  }, [shipments, fixed]);

  const shipmentsToShow = useMemo(() => {
    const paymentMode = shipmentFixed?.paymentModeID;
    if (paymentMode === undefined) {
      return [];
    }

    enum PaymentProcess {
      NO_STARTED = 0,
      STARTED = 1,
      FINISHED = 2,
    }

    const getPaymentProcess = (s: StoreShipmentInterface) => {
      if (s.paymentModeID === PaymentMode.COD && !s.documentNumber) {
        return PaymentProcess.NO_STARTED;
      } else if (
        s.paymentModeID === PaymentMode.COD &&
        (s.documentStatusID === DocumentStatus.PENDING ||
          s.documentStatusID === DocumentStatus.PARTIAL_PAID)
      ) {
        return PaymentProcess.STARTED;
      } else {
        return PaymentProcess.FINISHED;
      }
    };

    return shipments.filter(
      (s) =>
        s.shipmentHeaderID !== shipmentFixed?.shipmentHeaderID &&
        // All shipments must have the same consignee
        s.consigneeIdentificationNumber ===
          shipmentFixed?.consigneeIdentificationNumber &&
        // If the fixed shipment is exempt, then all the shipments
        // must be exempt
        (shipmentFixed.paymentModeID !== PaymentMode.EXEMPT ||
          s.paymentModeID === PaymentMode.EXEMPT) &&
        // All shipments must be in the same payment process
        getPaymentProcess(shipmentFixed) === getPaymentProcess(s) &&
        // If the fixed shipment is started, then all shipments must have the
        // same document
        (getPaymentProcess(shipmentFixed) !== PaymentProcess.STARTED ||
          shipmentFixed.documentNumber === s.documentNumber)
    );
  }, [shipments, shipmentFixed]);

  const allSelected = useMemo(() => {
    return shipments.every((shipment) =>
      selectedShipmentNumbers.some((n) => n === shipment.shipmentNumber)
    );
  }, [shipments, selectedShipmentNumbers]);

  return (
    <div className="flex flex-1 flex-col bg-white rounded-lg border px-8 pb-6 pt-4 overflow-hidden max-w-[80vw]">
      <div className="overflow-x-auto">
        <table className="table-auto w-full">
          <thead>
            <tr>
              <th className="text-left px-4 py-2 font-bold text-gray-700 text-xs w-5">
                <FormCheckbox
                  id={`select-all`}
                  name={`select-all`}
                  label=""
                  onChange={handleSelectAll}
                  checked={allSelected}
                />
              </th>

              <th className="text-left px-4 py-2 font-semibold text-xs">
                GUÍA
              </th>

              <th className="text-left px-4 py-2 font-semibold text-xs">
                REMITENTE
              </th>

              <th className="text-left px-4 py-2 font-semibold text-xs">
                ORIGEN
              </th>

              <th className="text-left px-4 py-2 font-semibold text-xs">
                DESTINATARIO
              </th>

              <th className="text-left px-4 py-2 font-semibold text-xs">
                ESTADO
              </th>
            </tr>
          </thead>
          <tbody>
            {!!shipmentFixed && (
              <tr className={classNames("hover:bg-gray-100 bg-gray-50")}>
                <td className="text-xs px-4 py-4 flex items-end justify-end">
                  <FormCheckbox
                    checked
                    label=""
                    onChange={() => {}}
                    id={`select-${shipmentFixed.shipmentNumber}`}
                    name={`select-${shipmentFixed.shipmentNumber}`}
                  />
                </td>

                <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left">
                  {shipmentFixed.shipmentNumber}
                </td>

                <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left truncate">
                  {formatName(shipmentFixed.shipperName)}
                </td>

                <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate">
                  {shipmentFixed.buCodeSource} -{" "}
                  {shipmentFixed.sourceBUAreaName}
                </td>

                <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate text-left truncate">
                  {formatName(shipmentFixed.consigneeName)}
                </td>

                <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate">
                  {shipmentStatusFormat(shipmentFixed.statusID)}
                </td>
              </tr>
            )}

            {shipmentsToShow.map((shipment, index) => {
              const selected = selectedShipmentNumbers.some(
                (n) => n === shipment.shipmentNumber
              );

              return (
                <tr
                  key={index}
                  className={classNames(
                    "hover:bg-gray-100",
                    index % 2 === 1 && "bg-gray-50"
                  )}
                >
                  <td className="text-xs px-4 py-4 flex items-end justify-end">
                    <FormCheckbox
                      id={`select-${shipment.shipmentNumber}-${index}`}
                      name={`select-${shipment.shipmentNumber}-${index}`}
                      label=""
                      onChange={() => {
                        if (selected) {
                          setSelectedShipmentNumbers(
                            selectedShipmentNumbers.filter(
                              (n) => n !== shipment.shipmentNumber
                            )
                          );
                        } else {
                          setSelectedShipmentNumbers([
                            ...selectedShipmentNumbers,
                            shipment.shipmentNumber,
                          ]);
                        }
                      }}
                      checked={selected}
                    />
                  </td>

                  <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left">
                    {shipment.shipmentNumber}
                  </td>

                  <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left truncate max-w-[15rem]">
                    {formatName(shipment.shipperName)}
                  </td>

                  <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate max-w-[15rem]">
                    {shipment.buCodeSource} - {shipment.sourceBUAreaName}
                  </td>

                  <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate text-left truncate max-w-[15rem]">
                    {formatName(shipment.consigneeName)}
                  </td>

                  <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate">
                    {shipmentStatusFormat(shipment.statusID)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShipmentTableCheck;
